'use client';

/**
 * API namespace `directMessages`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L8893-L9035).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const directMessages = {
  /**
   * GET /api/direct-messages/:otherUserId
   *
   * Fetches the bidirectional conversation history between the
   * authenticated user and `otherUserId`. Messages are auto-deleted
   * 48h after `createdAt` via a MongoDB TTL index in the backend —
   * the frontend never needs to handle deletion.
   *
   * Side-effect (backend): on a successful fetch, the backend marks
   * all messages FROM `otherUserId` TO the authenticated user as
   * read (`readAt = now`). We don't surface `readAt` on the frontend
   * — the ChatPanel only cares about display order + sender.
   *
   * @returns `{ messages: DirectMessageDTO[] }` sorted ascending by
   *          `createdAt` (oldest first — natural chat order).
   */
  getConversation(
    otherUserId: string,
    signal?: AbortSignal,
  ): Promise<{ messages: DirectMessageDTO[] }> {
    return apiFetch<{ messages: DirectMessageDTO[] }>(
      `/direct-messages/${encodeURIComponent(otherUserId)}`,
      { signal },
    );
  },
  /**
   * POST /api/direct-messages/:toUserId
   *
   * Sends a message from the authenticated user to `toUserId`. The
   * backend verifies that `toUserId` is a direct referral of the
   * sender (MatrixNode.directReferrals check) — non-direct referrals
   * get a 400 with a Spanish error message.
   *
   * Sprint 135 (Founder directive 2026-09-22): el `payload` ahora
   * soporta stickers (emoji) + archivos adjuntos (URL firmada). Al
   * menos uno de `text` / `sticker` / `fileUrl` debe estar presente.
   * Para enviar un archivo, primero se sube via `uploadFile()` —
   * el backend devuelve `{ fileUrl, fileName, fileType }` que se
   * pasan después a este método. NO se aceptan URLs arbitrarias
   * (el backend solo acepta URLs que él mismo emitió).
   *
   * On success, the backend ALSO emits a `direct-message:new`
   * WebSocket event to room `user:<toUserId>` (NOT to the sender —
   * the sender already has the message from this HTTP response). The
   * ChatPanel appends the returned message locally + the recipient
   * receives it in real time via WS. Adicionalmente, el backend
   * dispara una push notification fire-and-forget (Web Push RFC 8291)
   * para que el destinatario la vea aunque no tenga la app abierta.
   *
   * @returns the created message DTO (with server-assigned `messageId`
   *          + `createdAt` as ISO string).
   */
  send(
    toUserId: string,
    payload: SendDirectMessageInput,
    signal?: AbortSignal,
  ): Promise<DirectMessageDTO> {
    return apiFetch<DirectMessageDTO>(
      `/direct-messages/${encodeURIComponent(toUserId)}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        signal,
      },
    );
  },
  /**
   * POST /api/direct-messages/:toUserId/file (Sprint 135)
   *
   * Sube un archivo a MinIO/S3 y devuelve `{ fileUrl, fileName,
   * fileType, sizeBytes }`. El cliente pasa después `fileUrl` /
   * `fileName` / `fileType` al método `send()` para asociar el
   * archivo al mensaje.
   *
   * El backend valida:
   *   - Que `toUserId` sea un referido directo del remitente.
   *   - Tamaño ≤ 15 MB (elección operativa — el chat es efímero 48h).
   *   - MIME type en la whitelist operativa (imágenes, videos, audio,
   *     documentos comunes, ZIP). Excluye ejecutables + scripts.
   *
   * Multipart form-data con un único campo `file` (mirrors
   * `api.uploadAvatar(file)` pattern).
   *
   * @returns el DTO del archivo subido (URL firmada 15min TTL +
   *          metadata para enviar el mensaje).
   */
  uploadFile(
    toUserId: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<UploadedDirectFileDTO> {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch<UploadedDirectFileDTO>(
      `/direct-messages/${encodeURIComponent(toUserId)}/file`,
      {
        method: 'POST',
        body: formData,
        skipJsonStringify: true,
        signal,
      },
    );
  },
  /**
   * GET /api/direct-messages/unread/count
   *
   * Returns the count of unread direct messages addressed to the
   * authenticated user. Used by the sidebar badge on "Mis Directos"
   * (not yet wired — left for a future task).
   *
   * @returns `{ count: number }`.
   */
  getUnreadCount(signal?: AbortSignal): Promise<{ count: number }> {
    return apiFetch<{ count: number }>('/direct-messages/unread/count', {
      signal,
    });
  },
};
